import React from 'react';
import Select, { SingleValue } from 'react-select';
import './Autocomplete.styles.css';

type AutocompleteProps<T> = {
  options: Array<T>;
  valueKey?: keyof T;
  labelKey?: keyof T;
  placeholder?: string;
  clearable?: boolean;
  onChange: (value: T | null) => void;
  initialValue?: T | null;
};

type OptionType<T> = {
  value: T[keyof T];
  label: string;
  originalOption: T;
};

export const Autocomplete = <T,>({
  options,
  valueKey = 'value' as keyof T,
  labelKey = 'label' as keyof T,
  placeholder = 'Select...',
  clearable = false,
  onChange,
  initialValue = null,
}: AutocompleteProps<T>): React.JSX.Element => {
  const formattedOptions = options.map(option => ({
    value: option[valueKey],
    label: option[labelKey] as string,
    originalOption: option,
  }));

  const initialFormattedValue = React.useMemo(() => {
    if (!initialValue) {
      return null;
    }
    return (
      formattedOptions.find(option => option.originalOption[valueKey] === initialValue[valueKey]) ||
      null
    );
  }, [initialValue, valueKey, formattedOptions]);

  const handleChange = (selectedOption: SingleValue<OptionType<T>>): void => {
    onChange(selectedOption ? selectedOption.originalOption : null);
  };

  // Render the menu in a body-level portal so it isn't clipped by the overflow
  // of a scrollable/`overflow-hidden` container (e.g. a modal). Guarded for SSR.
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

  return (
    <div data-testid="autocomplete">
      <Select<OptionType<T>>
        value={initialFormattedValue}
        options={formattedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={clearable}
        classNamePrefix="react-select"
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        // eslint-disable-next-line no-magic-numbers
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  );
};

Autocomplete.displayName = 'Autocomplete';
